import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import AuthService from './AuthService';
import { API_BASE_URL } from 'config/api';
import { getActiveTenant } from './activeTenant';
import { isSessionLapsed } from './sessionLapse';

let _sessionExpiredDispatched = false;

/**
 * Dispatch a session-expired event (idempotent within a 2-second window
 * to avoid duplicate dispatches when several requests fail at once).
 */
export function dispatchSessionExpired(reason: string): void {
  if (_sessionExpiredDispatched) return;
  _sessionExpiredDispatched = true;

  window.dispatchEvent(new CustomEvent('session-expired', {
    detail: {
      message: 'Your session has expired. Please log in again.',
      reason,
    }
  }));

  setTimeout(() => { _sessionExpiredDispatched = false; }, 2000);
}

/**
 * Attach the auth token to the request. If no token is available,
 * dispatches session-expired and throws to prevent the request from being sent.
 *
 * During a lapse the request is still blocked, but silently: `getAuthToken()`
 * returns null the moment the token expires, so every screen still polling in
 * the background would otherwise re-announce the same dead session once per
 * request. The lapse has already been announced, and `ReauthDialog` is already
 * on screen.
 */
export function requireAuthToken(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const token = AuthService.getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  }
  if (!isSessionLapsed()) {
    dispatchSessionExpired('no_token');
  }
  throw new Error('No authentication token available');
}

/**
 * Handle auth-related response errors: 401 status or CORS/network errors
 * when no token is present (likely a 401 blocked by browser CORS policy).
 *
 * **This does not clear storage.** It used to call `AuthService.logout()` here,
 * which made it the site that actually ended sessions during work — `App.tsx`
 * is the one that gets talked about, but this is the one that fires. Wiping the
 * record before anyone has reacted left `AuthContext` with no identity to show
 * in the re-auth prompt and no record to renew. Announce it; let `AuthContext`
 * decide between a lapse and a real logout.
 */
export function handleAuthResponseError(error: any): void {
  if (error.response?.status === 401) {
    dispatchSessionExpired('token_invalid_or_expired');
  } else if (!error.response && !AuthService.getAuthToken()) {
    // No response object + no token → likely a 401 blocked by CORS
    dispatchSessionExpired('network_error_no_token');
  }
}

/**
 * Was this request cancelled by us, rather than failed?
 *
 * An aborted request is not a fault: the caller navigated away or started a
 * newer fetch. Callers use this to stay quiet instead of raising an error the
 * user did not cause. Covers both axios's `CanceledError` and the raw
 * `AbortError` a fetch-based path would surface.
 */
export function isAbortError(error: any): boolean {
  return (
    axios.isCancel?.(error) === true ||
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError' ||
    error?.name === 'AbortError'
  );
}

/**
 * Extract a meaningful error message from an API error response.
 * Checks error.response.data for string, .detail, or .message fields
 * before falling back to error.message or the provided fallback.
 */
export function extractApiError(error: any, fallbackMessage: string): Error {
  if (error.response?.data) {
    const errorData = error.response.data;
    if (typeof errorData === 'string') return new Error(errorData);
    if (errorData.detail) {
      // FastAPI validation errors (422) send detail as a list of
      // {loc, msg, type} objects — join the messages instead of letting
      // Error() stringify them to "[object Object]".
      if (Array.isArray(errorData.detail)) {
        const msgs = errorData.detail
          .map((d: any) => (typeof d === 'string' ? d : d?.msg))
          .filter(Boolean);
        if (msgs.length > 0) return new Error(msgs.join('; '));
      } else if (typeof errorData.detail === 'string') {
        return new Error(errorData.detail);
      } else if (typeof errorData.detail?.message === 'string') {
        // Structured detail objects (e.g. the teach confirm 409s:
        // {reason: "proof_expired", message: "..."}).
        return new Error(errorData.detail.message);
      }
    }
    if (typeof errorData.message === 'string') return new Error(errorData.message);
  }
  return new Error(error.message || fallbackMessage);
}

/**
 * Create an authenticated axios client with standardized interceptors.
 *
 * @param serviceName  Tag used in console logs, e.g. 'ORDER'
 * @param options.logging  Enable request/response/error console logging (default: true)
 */
export function createApiClient(
  serviceName: string,
  options?: { logging?: boolean },
): AxiosInstance {
  const logging = options?.logging ?? true;

  const client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
  });

  client.interceptors.request.use((config) => {
    requireAuthToken(config);
    // Tenant routing for the control-plane forwarder and the bound-tenant
    // check on a tenant container (see services/activeTenant.ts). An explicit
    // header on the request wins.
    const activeTenant = getActiveTenant();
    if (activeTenant && !config.headers['X-Tenant-Id']) {
      config.headers['X-Tenant-Id'] = activeTenant;
    }
    if (logging) {
      console.log(`📤 [${serviceName}] API Request:`, {
        method: config.method?.toUpperCase(),
        url: `${config.baseURL}${config.url}`,
        headers: {
          'Content-Type': config.headers['Content-Type'],
          'Authorization': config.headers.Authorization
            ? `${String(config.headers.Authorization).substring(0, 27)}...`
            : 'None',
        },
        data: config.data,
      });
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => {
      if (logging) {
        console.log(`📥 [${serviceName}] API Response:`, {
          method: response.config.method?.toUpperCase(),
          url: response.config.url,
          status: response.status,
          statusText: response.statusText,
          data: response.data,
        });
      }
      return response;
    },
    (error) => {
      if (logging) {
        console.error(`❌ [${serviceName}] API Error:`, {
          method: error.config?.method?.toUpperCase(),
          url: error.config?.url,
          status: error.response?.status,
          statusText: error.response?.statusText,
          message: error.message,
          data: error.response?.data,
        });
      }
      handleAuthResponseError(error);
      return Promise.reject(error);
    },
  );

  return client;
}
